import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Flex } from 'nallian-shared-ui/lib/components';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { useBoolState } from 'nallian-shared-ui/lib/hooks';
import { Table, Params, Merge, Menu, Actions } from 'nallian-table-builder-ui';
import apiRoutes from '../../../../app/api/routes';
import { ACTIONS, COMMUNITY_DETAIL_TABS } from '../../../communities/constants';
import { PRINCIPAL_ACTIONS, TABS } from '../../../organisations/constants';
import { INFINITE_SCROLL } from '../../constants';
import AddRoleAssignmentModal from './AddRoleAssignmentModal';
import removeRoleAssignmentService from '../../services/removeRoleAssignmentService';

// Only direct app-assignments can be removed; rows derived via user group or
// community subscription don't get the row action menu.
const DirectAssignmentMenu = ({ item, children }) =>
  item?.assignmentType?.toLowerCase() === 'direct' ? children : null;

DirectAssignmentMenu.propTypes = {
  item: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

const RoleAssignments = ({
  appId,
  installationId,
  featureId,
  roleId,
  isGlobal,
  dispatchRemoveRoleAssignmentDelete,
  removeRoleAssignmentFetch,
}) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen, setModalClosed] = useBoolState(false);
  const [removalItem, setRemovalItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const resource = `roleAssignments_${appId}_${installationId}_${featureId}_${roleId}`;

  const handleSetRemovalItem = useCallback((_, item) => setRemovalItem(item), [setRemovalItem]);
  const handleCancelRemove = useCallback(() => setRemovalItem(null), [setRemovalItem]);

  const handleRemove = useCallback(() => {
    if (removalItem) {
      dispatchRemoveRoleAssignmentDelete(
        appId,
        installationId,
        featureId,
        roleId,
        {
          communityId: isGlobal ? null : removalItem.community?.id,
          principalId: removalItem.principal?.id,
        },
        () => {
          setRemovalItem(null);
          setRefreshKey(key => key + 1);
        },
        t
      );
    }
  }, [
    removalItem,
    dispatchRemoveRoleAssignmentDelete,
    appId,
    installationId,
    featureId,
    roleId,
    isGlobal,
    t,
  ]);

  if (!appId || !installationId || !featureId || !roleId) {
    return null;
  }

  return (
    <Flex fluid direction="column">
      <Flex>
        <HasRight right="apps/instances/features/roles/assignments/create">
          <Button primary icon="plus" content={t('addUser')} onClick={setModalOpen} />
        </HasRight>
      </Flex>
      <Table
        key={refreshKey}
        resource={resource}
        url={apiRoutes.roleAssignments(appId, installationId, featureId, roleId)}
        noResultsMessage={t('noAssignmentsFound')}
        pageSize={50}
        infiniteScroll={INFINITE_SCROLL}
        idProp="principal.id"
        filtered
        sorted
        resizable
      >
        <Params.String
          name="assignmentType"
          label={t('assignmentType')}
          width="12rem"
          noSort
          noFilter
        />
        <Params.String
          name="principal.fullName"
          label={t('principalName')}
          sortName="principalName"
          filterName="principalName"
          width="20rem"
          link={`/organisations/:organisation.id/${TABS.PRINCIPALS}/${PRINCIPAL_ACTIONS.DETAILS}/:principal.id`}
          grow
        />
        <Params.String
          name="principal.primaryEmailAddress"
          label={t('email')}
          width="16rem"
          noSort
          noFilter
          grow
        />
        <Params.String
          name="organisation.name"
          label={t('organisation')}
          sortName="organisationName"
          filterName="organisationName"
          link={`/organisations/:organisation.id/${TABS.DETAILS}`}
          width="18rem"
          grow
        />
        <Params.String
          name="community.name"
          label={t('community')}
          sortName="communityName"
          filterName="communityName"
          link={`/communities/${ACTIONS.EDIT}/:community.id/${COMMUNITY_DETAIL_TABS.DETAILS}`}
          width="15rem"
        />
        <Params.String name="userGroup.name" label={t('userGroup')} width="15rem" noSort noFilter />
        <Merge
          width="4.5rem"
          align="center"
          hasRight="apps/instances/features/roles/assignments/delete"
          as={DirectAssignmentMenu}
        >
          <Menu width="3.5rem" align="center">
            <Actions.Button
              icon="trash"
              label={t('removeUser')}
              content={t('removeUser')}
              onClick={handleSetRemovalItem}
            />
          </Menu>
        </Merge>
      </Table>
      {modalOpen && (
        <AddRoleAssignmentModal
          appId={appId}
          installationId={installationId}
          featureId={featureId}
          roleId={roleId}
          isGlobal={isGlobal}
          resource={resource}
          onClose={setModalClosed}
        />
      )}
      <ConfirmModal
        open={!!removalItem}
        header={t('areYouSureRemoveRoleAssignment', {
          name: removalItem?.principal?.fullName,
          email: removalItem?.principal?.primaryEmailAddress,
        })}
        onConfirm={handleRemove}
        onCancel={handleCancelRemove}
        cancelContent={t('cancel')}
        confirmContent={t('remove')}
        confirmLoading={removeRoleAssignmentFetch.pending}
      />
    </Flex>
  );
};

RoleAssignments.propTypes = {
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  roleId: PropTypes.string.isRequired,
  isGlobal: PropTypes.bool,
  dispatchRemoveRoleAssignmentDelete: PropTypes.func.isRequired,
  removeRoleAssignmentFetch: PropTypes.object.isRequired,
};

RoleAssignments.defaultProps = { isGlobal: false };

export default compose(removeRoleAssignmentService)(RoleAssignments);
